<script lang="ts">
  import {setContext} from 'svelte';
  import {writable} from 'svelte/store';
  import emblaCarouselSvelte from 'embla-carousel-svelte';
  import type {EmblaCarouselType} from 'embla-carousel';
  import {carouselTvProps, type CarouselProps, type CarouselContext} from '.';
  import {IconButton} from 'Catalyst/components/IconButton';
  import {
    carouselSlotsLeftButtonGlyph,
    carouselSlotsRightButtonGlyph,
    carouselSlotsLeftButtonStyle,
    carouselSlotsRightButtonStyle,
    carouselSlotsRightButtonSize,
    carouselSlotsLeftButtonSize,
    carouselSlotsBaseGradientMask,
  } from 'Catalyst/tokens/output/carousel/carousel';

  export let selectedIndex: CarouselProps['selectedIndex'] = 1;

  $: carouselTvSlots = carouselTvProps();

  let embla: EmblaCarouselType | null = null;
  let lastSelectedIndex = selectedIndex;
  let containerRef = null;

  // Card registry for dynamic card management
  let nextCardId = 0;
  const cardRegistry = new Map<number, number>(); // cardId -> index

  const carouselStore = writable({
    selectedIndex,
    cardRegistry,
  });

  $: carouselStore.update(state => ({
    ...state,
    selectedIndex,
  }));

  // Handle external selectedIndex changes
  $: if (
    embla &&
    (selectedIndex !== lastSelectedIndex ||
      embla.selectedScrollSnap() !== selectedIndex)
  ) {
    lastSelectedIndex = selectedIndex;
    embla.scrollTo(selectedIndex);
  }

  $: canScrollPrev = $carouselStore.selectedIndex > 0;
  $: canScrollNext =
    $carouselStore.selectedIndex < $carouselStore.cardRegistry.size - 1;

  const scrollNext = () => {
    if (canScrollNext) {
      embla?.scrollNext();
    }
  };

  const scrollPrev = () => {
    if (canScrollPrev) {
      embla?.scrollPrev();
    }
  };

  const updateCardRegistryStore = () => {
    carouselStore.update(state => ({
      ...state,
      cardRegistry: new Map(cardRegistry),
    }));
  };

  const updateSelectedIndexStore = (newSelectedIndex: number) => {
    carouselStore.update(state => ({
      ...state,
      selectedIndex: newSelectedIndex,
    }));
  };

  const handleCardRegistration = () => {
    const cardId = nextCardId++;
    const cardIndex = cardRegistry.size;
    cardRegistry.set(cardId, cardIndex);

    updateCardRegistryStore();
    return {cardId, index: cardIndex};
  };

  const reindexCards = () => {
    const remainingCards = Array.from(cardRegistry.entries()).sort(
      (a, b) => a[1] - b[1]
    );

    cardRegistry.clear();
    remainingCards.forEach(([id], newIndex) => {
      cardRegistry.set(id, newIndex);
    });
  };

  const adjustSelectedIndex = (removedIndex: number) => {
    let newSelectedIndex = selectedIndex;
    if (removedIndex <= selectedIndex) {
      newSelectedIndex = Math.max(0, selectedIndex - 1);
    }
    newSelectedIndex = Math.min(newSelectedIndex, cardRegistry.size - 1);

    if (newSelectedIndex !== selectedIndex) {
      selectedIndex = newSelectedIndex;
      lastSelectedIndex = newSelectedIndex;
    }
    return newSelectedIndex;
  };

  const handleCardUnregistration = (cardId: number) => {
    const removedIndex = cardRegistry.get(cardId);
    if (removedIndex === undefined) return;
    cardRegistry.delete(cardId);
    reindexCards();
    const newSelectedIndex = adjustSelectedIndex(removedIndex);
    updateCardRegistryStore();
    updateSelectedIndexStore(newSelectedIndex);
  };

  const carouselContext: CarouselContext = {
    store: carouselStore,
    registerCard: handleCardRegistration,
    unregisterCard: handleCardUnregistration,
    scrollNext,
    scrollPrev,
  };

  $: canScroll = $carouselStore.cardRegistry.size > 1;

  setContext<CarouselContext>('carouselContext', carouselContext);

  const handleEmblaInit = (event: CustomEvent<EmblaCarouselType>) => {
    embla = event.detail;
    embla.scrollTo(selectedIndex, true);
    lastSelectedIndex = selectedIndex;

    embla.on('select', emblaInstance => {
      const newIndex = emblaInstance.selectedScrollSnap();
      selectedIndex = newIndex;
      lastSelectedIndex = newIndex;
    });
  };
</script>

<div class="relative">
  {#if canScroll}
    <div class="absolute left-[calc(25%-72px)] top-1/2 -translate-y-1/2 z-10">
      <IconButton
        icon={carouselSlotsLeftButtonGlyph}
        style={carouselSlotsLeftButtonStyle}
        size={carouselSlotsLeftButtonSize}
        on:click={scrollPrev}
        disabled={!canScrollPrev}
        tabindex={-1}
        aria-label="Previous"
      />
    </div>
  {/if}
  <div
    {...$$restProps}
    class={carouselTvSlots.base()}
    use:emblaCarouselSvelte={{options: {container: containerRef}, plugins: []}}
    on:emblaInit={handleEmblaInit}
    role="radiogroup"
    aria-label="Carousel"
    style="mask-image: {carouselSlotsBaseGradientMask}"
  >
    <div bind:this={containerRef} class={carouselTvSlots.container()}>
      <slot />
    </div>
  </div>
  {#if canScroll}
    <div class="absolute right-[calc(25%-72px)] top-1/2 -translate-y-1/2 z-10">
      <IconButton
        icon={carouselSlotsRightButtonGlyph}
        style={carouselSlotsRightButtonStyle}
        size={carouselSlotsRightButtonSize}
        on:click={scrollNext}
        disabled={!canScrollNext}
        tabindex={-1}
        aria-label="Next"
      />
    </div>
  {/if}
</div>
